/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * PeakRunning API specification
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AccountDeletionIncomplete,
  AccountDeletionPreview,
  AppReviewMode,
  AppReviewSignInBody,
  AppReviewSignInRefused,
  AppReviewSignInResult,
  AppleAccountToken,
  AppleNotificationBody,
  AppleNotificationResult,
  AppleTransactionBody,
  AppleTransactionRejected,
  AppleTransactionResult,
  AuthHealthStatus,
  BillingPortalSession,
  BillingPortalUnavailable,
  CaptainTransferRequest,
  Checkin,
  CopyScheduleEntryResult,
  CreateCheckinBody,
  CreateMemberBody,
  CreateScheduleEntryBody,
  CreateSoloCheckinBody,
  CreateSquadBody,
  CreateSquadResponse,
  CurrentUserInfo,
  Dashboard,
  GetMemberHistoryParams,
  GetRegionParams,
  HealthStatus,
  JoinSquadBody,
  JoinSquadResponse,
  ListCheckinsParams,
  ListMembersParams,
  ListSchedulesParams,
  Member,
  MemberHistory,
  MySubscriptionResponse,
  RegionStatus,
  RegionUnavailable,
  ScheduleEntry,
  SoloCheckinConflict,
  SoloCheckinResult,
  SoloCheckinUnavailable,
  Squad,
  SquadCreationBlockedResponse,
  SquadCreationCheck,
  SquadSummary,
  TransferCaptainBody,
  UpdateCheckinBody,
  UpdateMemberBody,
  UpdateScheduleEntryBody,
  UpdateSquadBody,
  WaitlistBody,
  WaitlistClosed,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Returns server health status
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns auth status for the authenticated user. Requires a valid Clerk session.
 * @summary Authenticated health check
 */
export const getAuthHealthCheckUrl = () => {
  return `/api/healthz/auth`;
};

export const authHealthCheck = async (
  options?: RequestInit,
): Promise<AuthHealthStatus> => {
  return customFetch<AuthHealthStatus>(getAuthHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getAuthHealthCheckQueryKey = () => {
  return [`/api/healthz/auth`] as const;
};

export const getAuthHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof authHealthCheck>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof authHealthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAuthHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof authHealthCheck>>> = ({
    signal,
  }) => authHealthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof authHealthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type AuthHealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof authHealthCheck>>
>;
export type AuthHealthCheckQueryError = ErrorType<void>;

/**
 * @summary Authenticated health check
 */

export function useAuthHealthCheck<
  TData = Awaited<ReturnType<typeof authHealthCheck>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof authHealthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getAuthHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * WI#44 — public, unauthenticated, still CORS-enabled for the peakrunning.com marketing site so a stale page sees the real answer. The waitlist closed at launch: every request answers 410 and nothing is stored. The waitlist_signups table is kept (empty).

 * @summary Retired — the marketing-site waitlist is closed
 */
export const getJoinWaitlistUrl = () => {
  return `/api/waitlist`;
};

export const joinWaitlist = async (
  waitlistBody?: WaitlistBody,
  options?: RequestInit,
): Promise<unknown> => {
  return customFetch<unknown>(getJoinWaitlistUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(waitlistBody),
  });
};

export const getJoinWaitlistMutationOptions = <
  TError = ErrorType<WaitlistClosed>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof joinWaitlist>>,
    TError,
    { data: BodyType<WaitlistBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof joinWaitlist>>,
  TError,
  { data: BodyType<WaitlistBody> },
  TContext
> => {
  const mutationKey = ["joinWaitlist"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof joinWaitlist>>,
    { data: BodyType<WaitlistBody> }
  > = (props) => {
    const { data } = props ?? {};

    return joinWaitlist(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type JoinWaitlistMutationResult = NonNullable<
  Awaited<ReturnType<typeof joinWaitlist>>
>;
export type JoinWaitlistMutationBody = BodyType<WaitlistBody>;
export type JoinWaitlistMutationError = ErrorType<WaitlistClosed>;

/**
 * @summary Retired — the marketing-site waitlist is closed
 */
export const useJoinWaitlist = <
  TError = ErrorType<WaitlistClosed>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof joinWaitlist>>,
    TError,
    { data: BodyType<WaitlistBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof joinWaitlist>>,
  TError,
  { data: BodyType<WaitlistBody> },
  TContext
> => {
  return useMutation(getJoinWaitlistMutationOptions(options));
};

/**
 * WI#43 — public, unauthenticated. The web app launches in the United States and Canada only; the sign-up page asks this before offering sign-up. Location comes from Cloudflare's CF-IPCountry on the request. reason lookup_failed means no usable country was present (recorded in Sentry) and new sign-ups are refused. reason origin_untrusted (#84) means no trusted Cloudflare header reached the API at all: our routing problem, recorded in Sentry, refused, and shown as a temporary failure. Outside production, test_country simulates a location for QA.
 * @summary Whether PeakRunning is available where this request comes from
 */
export const getGetRegionUrl = (params?: GetRegionParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/region?${stringifiedParams}`
    : `/api/region`;
};

export const getRegion = async (
  params?: GetRegionParams,
  options?: RequestInit,
): Promise<RegionStatus> => {
  return customFetch<RegionStatus>(getGetRegionUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetRegionQueryKey = (params?: GetRegionParams) => {
  return [`/api/region`, ...(params ? [params] : [])] as const;
};

export const getGetRegionQueryOptions = <
  TData = Awaited<ReturnType<typeof getRegion>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRegionParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRegion>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRegionQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRegion>>> = ({
    signal,
  }) => getRegion(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRegion>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetRegionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRegion>>
>;
export type GetRegionQueryError = ErrorType<unknown>;

/**
 * @summary Whether PeakRunning is available where this request comes from
 */

export function useGetRegion<
  TData = Awaited<ReturnType<typeof getRegion>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRegionParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRegion>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetRegionQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * #42 — the iPhone app shows "Sign in with password" only while this is true. Returns only the flag; nothing about the account or the window's dates.
 * @summary Whether an App Review sign-in window is open (public)
 */
export const getGetAppReviewModeUrl = () => {
  return `/api/auth/review-mode`;
};

export const getAppReviewMode = async (
  options?: RequestInit,
): Promise<AppReviewMode> => {
  return customFetch<AppReviewMode>(getGetAppReviewModeUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAppReviewModeQueryKey = () => {
  return [`/api/auth/review-mode`] as const;
};

export const getGetAppReviewModeQueryOptions = <
  TData = Awaited<ReturnType<typeof getAppReviewMode>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAppReviewMode>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAppReviewModeQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAppReviewMode>>
  > = ({ signal }) => getAppReviewMode({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAppReviewMode>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAppReviewModeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAppReviewMode>>
>;
export type GetAppReviewModeQueryError = ErrorType<unknown>;

/**
 * @summary Whether an App Review sign-in window is open (public)
 */

export function useGetAppReviewMode<
  TData = Awaited<ReturnType<typeof getAppReviewMode>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAppReviewMode>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAppReviewModeQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * #42 — a bounded bypass of Device Trust for ONE Clerk user (PEAKRUNNING_APP_REVIEW_USER_ID), live only while PEAKRUNNING_APP_REVIEW_UNTIL (ISO-8601 with Z) is in the future. The email must equal that account's primary email exactly; Clerk verifies the password. On success the response carries a single-use, 5-minute Clerk sign-in ticket in the body (never a URL). Every refusal is the same 401. Rate-limited per IP and per account; audited.
 * @summary App Review sign-in for the single allowlisted reviewer account (public)
 */
export const getAppReviewSignInUrl = () => {
  return `/api/auth/review-sign-in`;
};

export const appReviewSignIn = async (
  appReviewSignInBody: AppReviewSignInBody,
  options?: RequestInit,
): Promise<AppReviewSignInResult> => {
  return customFetch<AppReviewSignInResult>(getAppReviewSignInUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(appReviewSignInBody),
  });
};

export const getAppReviewSignInMutationOptions = <
  TError = ErrorType<AppReviewSignInRefused>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appReviewSignIn>>,
    TError,
    { data: BodyType<AppReviewSignInBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof appReviewSignIn>>,
  TError,
  { data: BodyType<AppReviewSignInBody> },
  TContext
> => {
  const mutationKey = ["appReviewSignIn"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof appReviewSignIn>>,
    { data: BodyType<AppReviewSignInBody> }
  > = (props) => {
    const { data } = props ?? {};

    return appReviewSignIn(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AppReviewSignInMutationResult = NonNullable<
  Awaited<ReturnType<typeof appReviewSignIn>>
>;
export type AppReviewSignInMutationBody = BodyType<AppReviewSignInBody>;
export type AppReviewSignInMutationError = ErrorType<AppReviewSignInRefused>;

/**
 * @summary App Review sign-in for the single allowlisted reviewer account (public)
 */
export const useAppReviewSignIn = <
  TError = ErrorType<AppReviewSignInRefused>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appReviewSignIn>>,
    TError,
    { data: BodyType<AppReviewSignInBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof appReviewSignIn>>,
  TError,
  { data: BodyType<AppReviewSignInBody> },
  TContext
> => {
  return useMutation(getAppReviewSignInMutationOptions(options));
};

/**
 * @summary Get current user info and their member records
 */
export const getGetCurrentUserUrl = () => {
  return `/api/me`;
};

export const getCurrentUser = async (
  options?: RequestInit,
): Promise<CurrentUserInfo> => {
  return customFetch<CurrentUserInfo>(getGetCurrentUserUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetCurrentUserQueryKey = () => {
  return [`/api/me`] as const;
};

export const getGetCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurrentUserQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({
    signal,
  }) => getCurrentUser({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetCurrentUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCurrentUser>>
>;
export type GetCurrentUserQueryError = ErrorType<unknown>;

/**
 * @summary Get current user info and their member records
 */

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCurrentUserQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * WI#34 — account deletion (Apple 5.1.1(v), web parity). Never refused because of a subscription. In order: (1) an active web-billed (source=peakcommerce) subscription is cancelled at period end through PeakCommerce; an Apple-billed one is never sent to PeakCommerce. (2) One transaction deletes the user's member rows (and with them their check-ins and schedule entries), deletes squads where they were the only member, hands captaincy of a squad with other runners to its longest-tenured runner, nulls pacesetter pointers, and tombstones the subscription row as deleted:row:<id>. (3) The Clerk user is deleted. 204 means all three happened. A 503 deletion_incomplete is retryable; a retry is idempotent and only finishes what is left.
 * @summary Delete the signed-in user's account, immediately and synchronously
 */
export const getDeleteCurrentUserUrl = () => {
  return `/api/me`;
};

export const deleteCurrentUser = async (
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteCurrentUserUrl(), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteCurrentUserMutationOptions = <
  TError = ErrorType<AccountDeletionIncomplete>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCurrentUser>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCurrentUser>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["deleteCurrentUser"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCurrentUser>>,
    void
  > = () => {
    return deleteCurrentUser(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCurrentUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteCurrentUser>>
>;

export type DeleteCurrentUserMutationError =
  ErrorType<AccountDeletionIncomplete>;

/**
 * @summary Delete the signed-in user's account, immediately and synchronously
 */
export const useDeleteCurrentUser = <
  TError = ErrorType<AccountDeletionIncomplete>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCurrentUser>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteCurrentUser>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getDeleteCurrentUserMutationOptions(options));
};

/**
 * #123 S5a. Server-authored subscription state for the Billing entry on web (S6b) and iPhone (S6c): plan, status, billing channel, when it renews or ends, whether a cancellation is scheduled, and where to manage it. Apple-billed subscriptions are managed in Apple's own settings; web-billed ones in the PeakCommerce portal via GET /billing/portal-session. subscription is null when the account has no subscription on record (an account has at most one subscription row).
 * @summary The signed-in runner's subscription, as the app should present it
 */
export const getGetMySubscriptionUrl = () => {
  return `/api/me/subscription`;
};

export const getMySubscription = async (
  options?: RequestInit,
): Promise<MySubscriptionResponse> => {
  return customFetch<MySubscriptionResponse>(getGetMySubscriptionUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetMySubscriptionQueryKey = () => {
  return [`/api/me/subscription`] as const;
};

export const getGetMySubscriptionQueryOptions = <
  TData = Awaited<ReturnType<typeof getMySubscription>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMySubscription>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMySubscriptionQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMySubscription>>
  > = ({ signal }) => getMySubscription({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMySubscription>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetMySubscriptionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMySubscription>>
>;
export type GetMySubscriptionQueryError = ErrorType<unknown>;

/**
 * @summary The signed-in runner's subscription, as the app should present it
 */

export function useGetMySubscription<
  TData = Awaited<ReturnType<typeof getMySubscription>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMySubscription>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetMySubscriptionQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * WI#34 — server-authored notices for the deletion confirm step, so the app never guesses billing channel or captaincy. Read-only; computed with the same plan DELETE /me executes.
 * @summary What deleting the signed-in user's account will do, for the confirm step
 */
export const getGetAccountDeletionPreviewUrl = () => {
  return `/api/me/deletion-preview`;
};

export const getAccountDeletionPreview = async (
  options?: RequestInit,
): Promise<AccountDeletionPreview> => {
  return customFetch<AccountDeletionPreview>(
    getGetAccountDeletionPreviewUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetAccountDeletionPreviewQueryKey = () => {
  return [`/api/me/deletion-preview`] as const;
};

export const getGetAccountDeletionPreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getAccountDeletionPreview>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAccountDeletionPreview>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAccountDeletionPreviewQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAccountDeletionPreview>>
  > = ({ signal }) => getAccountDeletionPreview({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAccountDeletionPreview>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAccountDeletionPreviewQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAccountDeletionPreview>>
>;
export type GetAccountDeletionPreviewQueryError = ErrorType<unknown>;

/**
 * @summary What deleting the signed-in user's account will do, for the confirm step
 */

export function useGetAccountDeletionPreview<
  TData = Awaited<ReturnType<typeof getAccountDeletionPreview>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAccountDeletionPreview>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAccountDeletionPreviewQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * WI#39 — a stable v5 UUID of the signed-in Clerk user id under a PeakRunning-specific namespace. A purchase carrying it is bound to this account; the server checks it before recording anything.
 * @summary The StoreKit appAccountToken the iPhone app must set on every purchase
 */
export const getGetAppleAccountTokenUrl = () => {
  return `/api/me/apple-account-token`;
};

export const getAppleAccountToken = async (
  options?: RequestInit,
): Promise<AppleAccountToken> => {
  return customFetch<AppleAccountToken>(getGetAppleAccountTokenUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAppleAccountTokenQueryKey = () => {
  return [`/api/me/apple-account-token`] as const;
};

export const getGetAppleAccountTokenQueryOptions = <
  TData = Awaited<ReturnType<typeof getAppleAccountToken>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAppleAccountToken>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAppleAccountTokenQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAppleAccountToken>>
  > = ({ signal }) => getAppleAccountToken({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAppleAccountToken>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAppleAccountTokenQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAppleAccountToken>>
>;
export type GetAppleAccountTokenQueryError = ErrorType<unknown>;

/**
 * @summary The StoreKit appAccountToken the iPhone app must set on every purchase
 */

export function useGetAppleAccountToken<
  TData = Awaited<ReturnType<typeof getAppleAccountToken>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAppleAccountToken>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAppleAccountTokenQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * WI#39 — the app sends the App Store's signed transaction (JWS). The server verifies it with Apple's App Store Server Library, checks it is a PeakRunning subscription bought by this account (appAccountToken), and records it as source=apple. The client is never trusted for entitlement: only the verified transaction decides. Recording into PeakCommerce (PC #675) is a separate step after that and never reverses it.
 * @summary Record a purchase or restore the iPhone app made through StoreKit 2
 */
export const getRecordAppleTransactionUrl = () => {
  return `/api/apple/transactions`;
};

export const recordAppleTransaction = async (
  appleTransactionBody: AppleTransactionBody,
  options?: RequestInit,
): Promise<AppleTransactionResult> => {
  return customFetch<AppleTransactionResult>(getRecordAppleTransactionUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(appleTransactionBody),
  });
};

export const getRecordAppleTransactionMutationOptions = <
  TError = ErrorType<AppleTransactionRejected>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof recordAppleTransaction>>,
    TError,
    { data: BodyType<AppleTransactionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof recordAppleTransaction>>,
  TError,
  { data: BodyType<AppleTransactionBody> },
  TContext
> => {
  const mutationKey = ["recordAppleTransaction"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof recordAppleTransaction>>,
    { data: BodyType<AppleTransactionBody> }
  > = (props) => {
    const { data } = props ?? {};

    return recordAppleTransaction(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RecordAppleTransactionMutationResult = NonNullable<
  Awaited<ReturnType<typeof recordAppleTransaction>>
>;
export type RecordAppleTransactionMutationBody = BodyType<AppleTransactionBody>;
export type RecordAppleTransactionMutationError =
  ErrorType<AppleTransactionRejected>;

/**
 * @summary Record a purchase or restore the iPhone app made through StoreKit 2
 */
export const useRecordAppleTransaction = <
  TError = ErrorType<AppleTransactionRejected>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof recordAppleTransaction>>,
    TError,
    { data: BodyType<AppleTransactionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof recordAppleTransaction>>,
  TError,
  { data: BodyType<AppleTransactionBody> },
  TContext
> => {
  return useMutation(getRecordAppleTransactionMutationOptions(options));
};

/**
 * WI#39 — the payload is a JWS signed by Apple; it is shape-checked, rate-limited per IP and verified before anything is read. Handles SUBSCRIBED, DID_RENEW, DID_FAIL_TO_RENEW, DID_CHANGE_RENEWAL_STATUS, EXPIRED, REFUND and REVOKE. 200 means handled or already seen; 503 means retry (Apple retries non-2xx).
 * @summary App Store Server Notifications v2 receiver (called by Apple, unauthenticated)
 */
export const getReceiveAppleNotificationUrl = () => {
  return `/api/apple/notifications`;
};

export const receiveAppleNotification = async (
  appleNotificationBody: AppleNotificationBody,
  options?: RequestInit,
): Promise<AppleNotificationResult> => {
  return customFetch<AppleNotificationResult>(
    getReceiveAppleNotificationUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(appleNotificationBody),
    },
  );
};

export const getReceiveAppleNotificationMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof receiveAppleNotification>>,
    TError,
    { data: BodyType<AppleNotificationBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof receiveAppleNotification>>,
  TError,
  { data: BodyType<AppleNotificationBody> },
  TContext
> => {
  const mutationKey = ["receiveAppleNotification"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof receiveAppleNotification>>,
    { data: BodyType<AppleNotificationBody> }
  > = (props) => {
    const { data } = props ?? {};

    return receiveAppleNotification(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReceiveAppleNotificationMutationResult = NonNullable<
  Awaited<ReturnType<typeof receiveAppleNotification>>
>;
export type ReceiveAppleNotificationMutationBody =
  BodyType<AppleNotificationBody>;
export type ReceiveAppleNotificationMutationError = ErrorType<void>;

/**
 * @summary App Store Server Notifications v2 receiver (called by Apple, unauthenticated)
 */
export const useReceiveAppleNotification = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof receiveAppleNotification>>,
    TError,
    { data: BodyType<AppleNotificationBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof receiveAppleNotification>>,
  TError,
  { data: BodyType<AppleNotificationBody> },
  TContext
> => {
  return useMutation(getReceiveAppleNotificationMutationOptions(options));
};

/**
 * WI#16 — the solo path. Identity comes from the Clerk session, never the body: the server provisions (or reuses) the caller's one rosterless runner profile and files the check-in under it with a null squad. One check-in per runner per date. Resending exactly what is already saved for that date is an idempotent replay (200, nothing written); a different check-in for a date that already has one is a 409 carrying the saved one.
 * @summary Log the signed-in runner's own check-in, with or without a squad
 */
export const getCreateSoloCheckinUrl = () => {
  return `/api/me/checkins`;
};

export const createSoloCheckin = async (
  createSoloCheckinBody: CreateSoloCheckinBody,
  options?: RequestInit,
): Promise<SoloCheckinResult> => {
  return customFetch<SoloCheckinResult>(getCreateSoloCheckinUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createSoloCheckinBody),
  });
};

export const getCreateSoloCheckinMutationOptions = <
  TError = ErrorType<
    void | RegionUnavailable | SoloCheckinConflict | SoloCheckinUnavailable
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSoloCheckin>>,
    TError,
    { data: BodyType<CreateSoloCheckinBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSoloCheckin>>,
  TError,
  { data: BodyType<CreateSoloCheckinBody> },
  TContext
> => {
  const mutationKey = ["createSoloCheckin"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSoloCheckin>>,
    { data: BodyType<CreateSoloCheckinBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createSoloCheckin(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSoloCheckinMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSoloCheckin>>
>;
export type CreateSoloCheckinMutationBody = BodyType<CreateSoloCheckinBody>;
export type CreateSoloCheckinMutationError = ErrorType<
  void | RegionUnavailable | SoloCheckinConflict | SoloCheckinUnavailable
>;

/**
 * @summary Log the signed-in runner's own check-in, with or without a squad
 */
export const useCreateSoloCheckin = <
  TError = ErrorType<
    void | RegionUnavailable | SoloCheckinConflict | SoloCheckinUnavailable
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSoloCheckin>>,
    TError,
    { data: BodyType<CreateSoloCheckinBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createSoloCheckin>>,
  TError,
  { data: BodyType<CreateSoloCheckinBody> },
  TContext
> => {
  return useMutation(getCreateSoloCheckinMutationOptions(options));
};

/**
 * #93 — read-only. Lets the first-run chooser show the paid prompt BEFORE the runner fills in a form, with the server-authored message and next step (checkout, Billing or the plan-change line) exactly as POST /squads' 402 would give them. Creates nothing.
 * @summary Whether this account may create a squad, and if not, the same blocked payload POST /squads would answer
 */
export const getGetSquadCreationCheckUrl = () => {
  return `/api/squads/creation-check`;
};

export const getSquadCreationCheck = async (
  options?: RequestInit,
): Promise<SquadCreationCheck> => {
  return customFetch<SquadCreationCheck>(getGetSquadCreationCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetSquadCreationCheckQueryKey = () => {
  return [`/api/squads/creation-check`] as const;
};

export const getGetSquadCreationCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof getSquadCreationCheck>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getSquadCreationCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSquadCreationCheckQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getSquadCreationCheck>>
  > = ({ signal }) => getSquadCreationCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSquadCreationCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetSquadCreationCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSquadCreationCheck>>
>;
export type GetSquadCreationCheckQueryError = ErrorType<void>;

/**
 * @summary Whether this account may create a squad, and if not, the same blocked payload POST /squads would answer
 */

export function useGetSquadCreationCheck<
  TData = Awaited<ReturnType<typeof getSquadCreationCheck>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getSquadCreationCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSquadCreationCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List all squads
 */
export const getListSquadsUrl = () => {
  return `/api/squads`;
};

export const listSquads = async (options?: RequestInit): Promise<Squad[]> => {
  return customFetch<Squad[]>(getListSquadsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListSquadsQueryKey = () => {
  return [`/api/squads`] as const;
};

export const getListSquadsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSquads>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listSquads>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSquadsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSquads>>> = ({
    signal,
  }) => listSquads({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSquads>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListSquadsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSquads>>
>;
export type ListSquadsQueryError = ErrorType<unknown>;

/**
 * @summary List all squads
 */

export function useListSquads<
  TData = Awaited<ReturnType<typeof listSquads>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listSquads>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListSquadsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a new squad
 */
export const getCreateSquadUrl = () => {
  return `/api/squads`;
};

export const createSquad = async (
  createSquadBody: CreateSquadBody,
  options?: RequestInit,
): Promise<CreateSquadResponse> => {
  return customFetch<CreateSquadResponse>(getCreateSquadUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createSquadBody),
  });
};

export const getCreateSquadMutationOptions = <
  TError = ErrorType<SquadCreationBlockedResponse | RegionUnavailable>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSquad>>,
    TError,
    { data: BodyType<CreateSquadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSquad>>,
  TError,
  { data: BodyType<CreateSquadBody> },
  TContext
> => {
  const mutationKey = ["createSquad"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSquad>>,
    { data: BodyType<CreateSquadBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createSquad(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSquadMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSquad>>
>;
export type CreateSquadMutationBody = BodyType<CreateSquadBody>;
export type CreateSquadMutationError = ErrorType<
  SquadCreationBlockedResponse | RegionUnavailable
>;

/**
 * @summary Create a new squad
 */
export const useCreateSquad = <
  TError = ErrorType<SquadCreationBlockedResponse | RegionUnavailable>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSquad>>,
    TError,
    { data: BodyType<CreateSquadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createSquad>>,
  TError,
  { data: BodyType<CreateSquadBody> },
  TContext
> => {
  return useMutation(getCreateSquadMutationOptions(options));
};

/**
 * @summary Join a squad via invite code
 */
export const getJoinSquadUrl = () => {
  return `/api/squads/join`;
};

export const joinSquad = async (
  joinSquadBody: JoinSquadBody,
  options?: RequestInit,
): Promise<JoinSquadResponse> => {
  return customFetch<JoinSquadResponse>(getJoinSquadUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(joinSquadBody),
  });
};

export const getJoinSquadMutationOptions = <
  TError = ErrorType<RegionUnavailable | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof joinSquad>>,
    TError,
    { data: BodyType<JoinSquadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof joinSquad>>,
  TError,
  { data: BodyType<JoinSquadBody> },
  TContext
> => {
  const mutationKey = ["joinSquad"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof joinSquad>>,
    { data: BodyType<JoinSquadBody> }
  > = (props) => {
    const { data } = props ?? {};

    return joinSquad(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type JoinSquadMutationResult = NonNullable<
  Awaited<ReturnType<typeof joinSquad>>
>;
export type JoinSquadMutationBody = BodyType<JoinSquadBody>;
export type JoinSquadMutationError = ErrorType<RegionUnavailable | void>;

/**
 * @summary Join a squad via invite code
 */
export const useJoinSquad = <
  TError = ErrorType<RegionUnavailable | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof joinSquad>>,
    TError,
    { data: BodyType<JoinSquadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof joinSquad>>,
  TError,
  { data: BodyType<JoinSquadBody> },
  TContext
> => {
  return useMutation(getJoinSquadMutationOptions(options));
};

/**
 * @summary Get a squad by ID
 */
export const getGetSquadUrl = (id: number) => {
  return `/api/squads/${id}`;
};

export const getSquad = async (
  id: number,
  options?: RequestInit,
): Promise<Squad> => {
  return customFetch<Squad>(getGetSquadUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetSquadQueryKey = (id: number) => {
  return [`/api/squads/${id}`] as const;
};

export const getGetSquadQueryOptions = <
  TData = Awaited<ReturnType<typeof getSquad>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSquad>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSquadQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSquad>>> = ({
    signal,
  }) => getSquad(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSquad>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetSquadQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSquad>>
>;
export type GetSquadQueryError = ErrorType<void>;

/**
 * @summary Get a squad by ID
 */

export function useGetSquad<
  TData = Awaited<ReturnType<typeof getSquad>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSquad>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSquadQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a squad
 */
export const getUpdateSquadUrl = (id: number) => {
  return `/api/squads/${id}`;
};

export const updateSquad = async (
  id: number,
  updateSquadBody: UpdateSquadBody,
  options?: RequestInit,
): Promise<Squad> => {
  return customFetch<Squad>(getUpdateSquadUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateSquadBody),
  });
};

export const getUpdateSquadMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSquad>>,
    TError,
    { id: number; data: BodyType<UpdateSquadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateSquad>>,
  TError,
  { id: number; data: BodyType<UpdateSquadBody> },
  TContext
> => {
  const mutationKey = ["updateSquad"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateSquad>>,
    { id: number; data: BodyType<UpdateSquadBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateSquad(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateSquadMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateSquad>>
>;
export type UpdateSquadMutationBody = BodyType<UpdateSquadBody>;
export type UpdateSquadMutationError = ErrorType<void>;

/**
 * @summary Update a squad
 */
export const useUpdateSquad = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSquad>>,
    TError,
    { id: number; data: BodyType<UpdateSquadBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateSquad>>,
  TError,
  { id: number; data: BodyType<UpdateSquadBody> },
  TContext
> => {
  return useMutation(getUpdateSquadMutationOptions(options));
};

/**
 * @summary Delete a squad
 */
export const getDeleteSquadUrl = (id: number) => {
  return `/api/squads/${id}`;
};

export const deleteSquad = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteSquadUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteSquadMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSquad>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSquad>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteSquad"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSquad>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteSquad(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteSquadMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSquad>>
>;

export type DeleteSquadMutationError = ErrorType<void>;

/**
 * @summary Delete a squad
 */
export const useDeleteSquad = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSquad>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteSquad>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteSquadMutationOptions(options));
};

/**
 * @summary Get squad activity summary (adherence, streak, member stats)
 */
export const getGetSquadSummaryUrl = (id: number) => {
  return `/api/squads/${id}/summary`;
};

export const getSquadSummary = async (
  id: number,
  options?: RequestInit,
): Promise<SquadSummary> => {
  return customFetch<SquadSummary>(getGetSquadSummaryUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetSquadSummaryQueryKey = (id: number) => {
  return [`/api/squads/${id}/summary`] as const;
};

export const getGetSquadSummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof getSquadSummary>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSquadSummary>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSquadSummaryQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSquadSummary>>> = ({
    signal,
  }) => getSquadSummary(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSquadSummary>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetSquadSummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSquadSummary>>
>;
export type GetSquadSummaryQueryError = ErrorType<void>;

/**
 * @summary Get squad activity summary (adherence, streak, member stats)
 */

export function useGetSquadSummary<
  TData = Awaited<ReturnType<typeof getSquadSummary>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSquadSummary>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSquadSummaryQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Regenerate the squad's invite code (captain or admin only)
 */
export const getRegenerateInviteCodeUrl = (id: number) => {
  return `/api/squads/${id}/regenerate-invite`;
};

export const regenerateInviteCode = async (
  id: number,
  options?: RequestInit,
): Promise<Squad> => {
  return customFetch<Squad>(getRegenerateInviteCodeUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getRegenerateInviteCodeMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof regenerateInviteCode>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof regenerateInviteCode>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["regenerateInviteCode"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof regenerateInviteCode>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return regenerateInviteCode(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RegenerateInviteCodeMutationResult = NonNullable<
  Awaited<ReturnType<typeof regenerateInviteCode>>
>;

export type RegenerateInviteCodeMutationError = ErrorType<void>;

/**
 * @summary Regenerate the squad's invite code (captain or admin only)
 */
export const useRegenerateInviteCode = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof regenerateInviteCode>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof regenerateInviteCode>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getRegenerateInviteCodeMutationOptions(options));
};

/**
 * @summary Transfer captaincy to another runner in the squad
 */
export const getTransferCaptainUrl = (id: number) => {
  return `/api/squads/${id}/transfer-captain`;
};

export const transferCaptain = async (
  id: number,
  transferCaptainBody: TransferCaptainBody,
  options?: RequestInit,
): Promise<Member> => {
  return customFetch<Member>(getTransferCaptainUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(transferCaptainBody),
  });
};

export const getTransferCaptainMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof transferCaptain>>,
    TError,
    { id: number; data: BodyType<TransferCaptainBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof transferCaptain>>,
  TError,
  { id: number; data: BodyType<TransferCaptainBody> },
  TContext
> => {
  const mutationKey = ["transferCaptain"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof transferCaptain>>,
    { id: number; data: BodyType<TransferCaptainBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return transferCaptain(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type TransferCaptainMutationResult = NonNullable<
  Awaited<ReturnType<typeof transferCaptain>>
>;
export type TransferCaptainMutationBody = BodyType<TransferCaptainBody>;
export type TransferCaptainMutationError = ErrorType<void>;

/**
 * @summary Transfer captaincy to another runner in the squad
 */
export const useTransferCaptain = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof transferCaptain>>,
    TError,
    { id: number; data: BodyType<TransferCaptainBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof transferCaptain>>,
  TError,
  { id: number; data: BodyType<TransferCaptainBody> },
  TContext
> => {
  return useMutation(getTransferCaptainMutationOptions(options));
};

/**
 * @summary Request captain transfer (when captain is inactive 14+ days)
 */
export const getCreateTransferRequestUrl = (id: number) => {
  return `/api/squads/${id}/transfer-request`;
};

export const createTransferRequest = async (
  id: number,
  options?: RequestInit,
): Promise<CaptainTransferRequest> => {
  return customFetch<CaptainTransferRequest>(getCreateTransferRequestUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getCreateTransferRequestMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTransferRequest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTransferRequest>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["createTransferRequest"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTransferRequest>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return createTransferRequest(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTransferRequestMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTransferRequest>>
>;

export type CreateTransferRequestMutationError = ErrorType<void>;

/**
 * @summary Request captain transfer (when captain is inactive 14+ days)
 */
export const useCreateTransferRequest = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTransferRequest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTransferRequest>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getCreateTransferRequestMutationOptions(options));
};

/**
 * @summary Captain accepts the transfer request (immediately transfers to longest-tenured runner)
 */
export const getAcceptTransferRequestUrl = (id: number) => {
  return `/api/squads/${id}/transfer-request/accept`;
};

export const acceptTransferRequest = async (
  id: number,
  options?: RequestInit,
): Promise<Member> => {
  return customFetch<Member>(getAcceptTransferRequestUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getAcceptTransferRequestMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acceptTransferRequest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof acceptTransferRequest>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["acceptTransferRequest"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof acceptTransferRequest>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return acceptTransferRequest(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AcceptTransferRequestMutationResult = NonNullable<
  Awaited<ReturnType<typeof acceptTransferRequest>>
>;

export type AcceptTransferRequestMutationError = ErrorType<void>;

/**
 * @summary Captain accepts the transfer request (immediately transfers to longest-tenured runner)
 */
export const useAcceptTransferRequest = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acceptTransferRequest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof acceptTransferRequest>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getAcceptTransferRequestMutationOptions(options));
};

/**
 * @summary Captain dismisses the transfer request (30-day cooldown)
 */
export const getDismissTransferRequestUrl = (id: number) => {
  return `/api/squads/${id}/transfer-request/dismiss`;
};

export const dismissTransferRequest = async (
  id: number,
  options?: RequestInit,
): Promise<CaptainTransferRequest> => {
  return customFetch<CaptainTransferRequest>(getDismissTransferRequestUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getDismissTransferRequestMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof dismissTransferRequest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof dismissTransferRequest>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["dismissTransferRequest"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof dismissTransferRequest>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return dismissTransferRequest(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DismissTransferRequestMutationResult = NonNullable<
  Awaited<ReturnType<typeof dismissTransferRequest>>
>;

export type DismissTransferRequestMutationError = ErrorType<void>;

/**
 * @summary Captain dismisses the transfer request (30-day cooldown)
 */
export const useDismissTransferRequest = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof dismissTransferRequest>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof dismissTransferRequest>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDismissTransferRequestMutationOptions(options));
};

/**
 * @summary List all members (optionally filter by squadId)
 */
export const getListMembersUrl = (params?: ListMembersParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/members?${stringifiedParams}`
    : `/api/members`;
};

export const listMembers = async (
  params?: ListMembersParams,
  options?: RequestInit,
): Promise<Member[]> => {
  return customFetch<Member[]>(getListMembersUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListMembersQueryKey = (params?: ListMembersParams) => {
  return [`/api/members`, ...(params ? [params] : [])] as const;
};

export const getListMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<unknown>,
>(
  params?: ListMembersParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMembers>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMembersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMembers>>> = ({
    signal,
  }) => listMembers(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMembers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMembers>>
>;
export type ListMembersQueryError = ErrorType<unknown>;

/**
 * @summary List all members (optionally filter by squadId)
 */

export function useListMembers<
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<unknown>,
>(
  params?: ListMembersParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMembers>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMembersQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a new member (join a squad)
 */
export const getCreateMemberUrl = () => {
  return `/api/members`;
};

export const createMember = async (
  createMemberBody: CreateMemberBody,
  options?: RequestInit,
): Promise<Member> => {
  return customFetch<Member>(getCreateMemberUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createMemberBody),
  });
};

export const getCreateMemberMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMember>>,
    TError,
    { data: BodyType<CreateMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMember>>,
  TError,
  { data: BodyType<CreateMemberBody> },
  TContext
> => {
  const mutationKey = ["createMember"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMember>>,
    { data: BodyType<CreateMemberBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createMember(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMember>>
>;
export type CreateMemberMutationBody = BodyType<CreateMemberBody>;
export type CreateMemberMutationError = ErrorType<unknown>;

/**
 * @summary Create a new member (join a squad)
 */
export const useCreateMember = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMember>>,
    TError,
    { data: BodyType<CreateMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMember>>,
  TError,
  { data: BodyType<CreateMemberBody> },
  TContext
> => {
  return useMutation(getCreateMemberMutationOptions(options));
};

/**
 * @summary Get a member by ID
 */
export const getGetMemberUrl = (id: number) => {
  return `/api/members/${id}`;
};

export const getMember = async (
  id: number,
  options?: RequestInit,
): Promise<Member> => {
  return customFetch<Member>(getGetMemberUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetMemberQueryKey = (id: number) => {
  return [`/api/members/${id}`] as const;
};

export const getGetMemberQueryOptions = <
  TData = Awaited<ReturnType<typeof getMember>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMember>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMemberQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMember>>> = ({
    signal,
  }) => getMember(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getMember>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetMemberQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMember>>
>;
export type GetMemberQueryError = ErrorType<void>;

/**
 * @summary Get a member by ID
 */

export function useGetMember<
  TData = Awaited<ReturnType<typeof getMember>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMember>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetMemberQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a member
 */
export const getUpdateMemberUrl = (id: number) => {
  return `/api/members/${id}`;
};

export const updateMember = async (
  id: number,
  updateMemberBody: UpdateMemberBody,
  options?: RequestInit,
): Promise<Member> => {
  return customFetch<Member>(getUpdateMemberUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateMemberBody),
  });
};

export const getUpdateMemberMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMember>>,
    TError,
    { id: number; data: BodyType<UpdateMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMember>>,
  TError,
  { id: number; data: BodyType<UpdateMemberBody> },
  TContext
> => {
  const mutationKey = ["updateMember"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMember>>,
    { id: number; data: BodyType<UpdateMemberBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateMember(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMember>>
>;
export type UpdateMemberMutationBody = BodyType<UpdateMemberBody>;
export type UpdateMemberMutationError = ErrorType<void>;

/**
 * @summary Update a member
 */
export const useUpdateMember = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMember>>,
    TError,
    { id: number; data: BodyType<UpdateMemberBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMember>>,
  TError,
  { id: number; data: BodyType<UpdateMemberBody> },
  TContext
> => {
  return useMutation(getUpdateMemberMutationOptions(options));
};

/**
 * @summary Remove a member from the squad
 */
export const getDeleteMemberUrl = (id: number) => {
  return `/api/members/${id}`;
};

export const deleteMember = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMemberUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMemberMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMember>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMember>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteMember"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMember>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteMember(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMember>>
>;

export type DeleteMemberMutationError = ErrorType<void>;

/**
 * @summary Remove a member from the squad
 */
export const useDeleteMember = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMember>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMember>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteMemberMutationOptions(options));
};

/**
 * @summary A runner's check-in history, paged, with the planned session beside each run (WI#23 S4)
 */
export const getGetMemberHistoryUrl = (
  id: number,
  params?: GetMemberHistoryParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/members/${id}/history?${stringifiedParams}`
    : `/api/members/${id}/history`;
};

export const getMemberHistory = async (
  id: number,
  params?: GetMemberHistoryParams,
  options?: RequestInit,
): Promise<MemberHistory> => {
  return customFetch<MemberHistory>(getGetMemberHistoryUrl(id, params), {
    ...options,
    method: "GET",
  });
};

export const getGetMemberHistoryQueryKey = (
  id: number,
  params?: GetMemberHistoryParams,
) => {
  return [`/api/members/${id}/history`, ...(params ? [params] : [])] as const;
};

export const getGetMemberHistoryQueryOptions = <
  TData = Awaited<ReturnType<typeof getMemberHistory>>,
  TError = ErrorType<void>,
>(
  id: number,
  params?: GetMemberHistoryParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMemberHistory>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetMemberHistoryQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMemberHistory>>
  > = ({ signal }) =>
    getMemberHistory(id, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getMemberHistory>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetMemberHistoryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMemberHistory>>
>;
export type GetMemberHistoryQueryError = ErrorType<void>;

/**
 * @summary A runner's check-in history, paged, with the planned session beside each run (WI#23 S4)
 */

export function useGetMemberHistory<
  TData = Awaited<ReturnType<typeof getMemberHistory>>,
  TError = ErrorType<void>,
>(
  id: number,
  params?: GetMemberHistoryParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMemberHistory>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetMemberHistoryQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List check-ins (filter by memberId or squadId)
 */
export const getListCheckinsUrl = (params?: ListCheckinsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/checkins?${stringifiedParams}`
    : `/api/checkins`;
};

export const listCheckins = async (
  params?: ListCheckinsParams,
  options?: RequestInit,
): Promise<Checkin[]> => {
  return customFetch<Checkin[]>(getListCheckinsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListCheckinsQueryKey = (params?: ListCheckinsParams) => {
  return [`/api/checkins`, ...(params ? [params] : [])] as const;
};

export const getListCheckinsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCheckins>>,
  TError = ErrorType<unknown>,
>(
  params?: ListCheckinsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCheckins>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCheckinsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCheckins>>> = ({
    signal,
  }) => listCheckins(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCheckins>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCheckinsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCheckins>>
>;
export type ListCheckinsQueryError = ErrorType<unknown>;

/**
 * @summary List check-ins (filter by memberId or squadId)
 */

export function useListCheckins<
  TData = Awaited<ReturnType<typeof listCheckins>>,
  TError = ErrorType<unknown>,
>(
  params?: ListCheckinsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCheckins>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCheckinsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Submit a daily check-in (Crushed It or Skipped)
 */
export const getCreateCheckinUrl = () => {
  return `/api/checkins`;
};

export const createCheckin = async (
  createCheckinBody: CreateCheckinBody,
  options?: RequestInit,
): Promise<Checkin> => {
  return customFetch<Checkin>(getCreateCheckinUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createCheckinBody),
  });
};

export const getCreateCheckinMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCheckin>>,
    TError,
    { data: BodyType<CreateCheckinBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCheckin>>,
  TError,
  { data: BodyType<CreateCheckinBody> },
  TContext
> => {
  const mutationKey = ["createCheckin"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCheckin>>,
    { data: BodyType<CreateCheckinBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createCheckin(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCheckinMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCheckin>>
>;
export type CreateCheckinMutationBody = BodyType<CreateCheckinBody>;
export type CreateCheckinMutationError = ErrorType<unknown>;

/**
 * @summary Submit a daily check-in (Crushed It or Skipped)
 */
export const useCreateCheckin = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCheckin>>,
    TError,
    { data: BodyType<CreateCheckinBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createCheckin>>,
  TError,
  { data: BodyType<CreateCheckinBody> },
  TContext
> => {
  return useMutation(getCreateCheckinMutationOptions(options));
};

/**
 * @summary Get a check-in by ID
 */
export const getGetCheckinUrl = (id: number) => {
  return `/api/checkins/${id}`;
};

export const getCheckin = async (
  id: number,
  options?: RequestInit,
): Promise<Checkin> => {
  return customFetch<Checkin>(getGetCheckinUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetCheckinQueryKey = (id: number) => {
  return [`/api/checkins/${id}`] as const;
};

export const getGetCheckinQueryOptions = <
  TData = Awaited<ReturnType<typeof getCheckin>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCheckin>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCheckinQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCheckin>>> = ({
    signal,
  }) => getCheckin(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getCheckin>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetCheckinQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCheckin>>
>;
export type GetCheckinQueryError = ErrorType<void>;

/**
 * @summary Get a check-in by ID
 */

export function useGetCheckin<
  TData = Awaited<ReturnType<typeof getCheckin>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCheckin>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCheckinQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a check-in (e.g. change status or note)
 */
export const getUpdateCheckinUrl = (id: number) => {
  return `/api/checkins/${id}`;
};

export const updateCheckin = async (
  id: number,
  updateCheckinBody: UpdateCheckinBody,
  options?: RequestInit,
): Promise<Checkin> => {
  return customFetch<Checkin>(getUpdateCheckinUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateCheckinBody),
  });
};

export const getUpdateCheckinMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateCheckin>>,
    TError,
    { id: number; data: BodyType<UpdateCheckinBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateCheckin>>,
  TError,
  { id: number; data: BodyType<UpdateCheckinBody> },
  TContext
> => {
  const mutationKey = ["updateCheckin"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateCheckin>>,
    { id: number; data: BodyType<UpdateCheckinBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateCheckin(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCheckinMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateCheckin>>
>;
export type UpdateCheckinMutationBody = BodyType<UpdateCheckinBody>;
export type UpdateCheckinMutationError = ErrorType<void>;

/**
 * @summary Update a check-in (e.g. change status or note)
 */
export const useUpdateCheckin = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateCheckin>>,
    TError,
    { id: number; data: BodyType<UpdateCheckinBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateCheckin>>,
  TError,
  { id: number; data: BodyType<UpdateCheckinBody> },
  TContext
> => {
  return useMutation(getUpdateCheckinMutationOptions(options));
};

/**
 * @summary Delete a check-in
 */
export const getDeleteCheckinUrl = (id: number) => {
  return `/api/checkins/${id}`;
};

export const deleteCheckin = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteCheckinUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteCheckinMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCheckin>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCheckin>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteCheckin"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCheckin>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteCheckin(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCheckinMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteCheckin>>
>;

export type DeleteCheckinMutationError = ErrorType<void>;

/**
 * @summary Delete a check-in
 */
export const useDeleteCheckin = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCheckin>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteCheckin>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteCheckinMutationOptions(options));
};

/**
 * @summary List training schedule entries (filter by squadId and/or memberId)
 */
export const getListSchedulesUrl = (params?: ListSchedulesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/schedules?${stringifiedParams}`
    : `/api/schedules`;
};

export const listSchedules = async (
  params?: ListSchedulesParams,
  options?: RequestInit,
): Promise<ScheduleEntry[]> => {
  return customFetch<ScheduleEntry[]>(getListSchedulesUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListSchedulesQueryKey = (params?: ListSchedulesParams) => {
  return [`/api/schedules`, ...(params ? [params] : [])] as const;
};

export const getListSchedulesQueryOptions = <
  TData = Awaited<ReturnType<typeof listSchedules>>,
  TError = ErrorType<unknown>,
>(
  params?: ListSchedulesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSchedules>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSchedulesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSchedules>>> = ({
    signal,
  }) => listSchedules(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSchedules>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListSchedulesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSchedules>>
>;
export type ListSchedulesQueryError = ErrorType<unknown>;

/**
 * @summary List training schedule entries (filter by squadId and/or memberId)
 */

export function useListSchedules<
  TData = Awaited<ReturnType<typeof listSchedules>>,
  TError = ErrorType<unknown>,
>(
  params?: ListSchedulesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listSchedules>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListSchedulesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a training schedule entry for a squad
 */
export const getCreateScheduleEntryUrl = () => {
  return `/api/schedules`;
};

export const createScheduleEntry = async (
  createScheduleEntryBody: CreateScheduleEntryBody,
  options?: RequestInit,
): Promise<ScheduleEntry> => {
  return customFetch<ScheduleEntry>(getCreateScheduleEntryUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createScheduleEntryBody),
  });
};

export const getCreateScheduleEntryMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createScheduleEntry>>,
    TError,
    { data: BodyType<CreateScheduleEntryBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createScheduleEntry>>,
  TError,
  { data: BodyType<CreateScheduleEntryBody> },
  TContext
> => {
  const mutationKey = ["createScheduleEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createScheduleEntry>>,
    { data: BodyType<CreateScheduleEntryBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createScheduleEntry(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateScheduleEntryMutationResult = NonNullable<
  Awaited<ReturnType<typeof createScheduleEntry>>
>;
export type CreateScheduleEntryMutationBody = BodyType<CreateScheduleEntryBody>;
export type CreateScheduleEntryMutationError = ErrorType<unknown>;

/**
 * @summary Create a training schedule entry for a squad
 */
export const useCreateScheduleEntry = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createScheduleEntry>>,
    TError,
    { data: BodyType<CreateScheduleEntryBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createScheduleEntry>>,
  TError,
  { data: BodyType<CreateScheduleEntryBody> },
  TContext
> => {
  return useMutation(getCreateScheduleEntryMutationOptions(options));
};

/**
 * #96 — same validation and authorisation as POST /schedules, but idempotent per entry and atomic: if an entry already exists for the same squad, runner (or squad-wide), week, day and session type (trimmed, case-insensitive), nothing is inserted and the existing one is returned with alreadyExisted true. Concurrent copies of the same entry never duplicate it.
 * @summary Copy one schedule entry into a week, idempotently (Copy Week)
 */
export const getCopyScheduleEntryUrl = () => {
  return `/api/schedules/copy-entry`;
};

export const copyScheduleEntry = async (
  createScheduleEntryBody: CreateScheduleEntryBody,
  options?: RequestInit,
): Promise<CopyScheduleEntryResult> => {
  return customFetch<CopyScheduleEntryResult>(getCopyScheduleEntryUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createScheduleEntryBody),
  });
};

export const getCopyScheduleEntryMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof copyScheduleEntry>>,
    TError,
    { data: BodyType<CreateScheduleEntryBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof copyScheduleEntry>>,
  TError,
  { data: BodyType<CreateScheduleEntryBody> },
  TContext
> => {
  const mutationKey = ["copyScheduleEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof copyScheduleEntry>>,
    { data: BodyType<CreateScheduleEntryBody> }
  > = (props) => {
    const { data } = props ?? {};

    return copyScheduleEntry(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CopyScheduleEntryMutationResult = NonNullable<
  Awaited<ReturnType<typeof copyScheduleEntry>>
>;
export type CopyScheduleEntryMutationBody = BodyType<CreateScheduleEntryBody>;
export type CopyScheduleEntryMutationError = ErrorType<unknown>;

/**
 * @summary Copy one schedule entry into a week, idempotently (Copy Week)
 */
export const useCopyScheduleEntry = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof copyScheduleEntry>>,
    TError,
    { data: BodyType<CreateScheduleEntryBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof copyScheduleEntry>>,
  TError,
  { data: BodyType<CreateScheduleEntryBody> },
  TContext
> => {
  return useMutation(getCopyScheduleEntryMutationOptions(options));
};

/**
 * @summary Update a schedule entry
 */
export const getUpdateScheduleEntryUrl = (id: number) => {
  return `/api/schedules/${id}`;
};

export const updateScheduleEntry = async (
  id: number,
  updateScheduleEntryBody: UpdateScheduleEntryBody,
  options?: RequestInit,
): Promise<ScheduleEntry> => {
  return customFetch<ScheduleEntry>(getUpdateScheduleEntryUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateScheduleEntryBody),
  });
};

export const getUpdateScheduleEntryMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateScheduleEntry>>,
    TError,
    { id: number; data: BodyType<UpdateScheduleEntryBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateScheduleEntry>>,
  TError,
  { id: number; data: BodyType<UpdateScheduleEntryBody> },
  TContext
> => {
  const mutationKey = ["updateScheduleEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateScheduleEntry>>,
    { id: number; data: BodyType<UpdateScheduleEntryBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateScheduleEntry(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateScheduleEntryMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateScheduleEntry>>
>;
export type UpdateScheduleEntryMutationBody = BodyType<UpdateScheduleEntryBody>;
export type UpdateScheduleEntryMutationError = ErrorType<void>;

/**
 * @summary Update a schedule entry
 */
export const useUpdateScheduleEntry = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateScheduleEntry>>,
    TError,
    { id: number; data: BodyType<UpdateScheduleEntryBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateScheduleEntry>>,
  TError,
  { id: number; data: BodyType<UpdateScheduleEntryBody> },
  TContext
> => {
  return useMutation(getUpdateScheduleEntryMutationOptions(options));
};

/**
 * @summary Delete a schedule entry
 */
export const getDeleteScheduleEntryUrl = (id: number) => {
  return `/api/schedules/${id}`;
};

export const deleteScheduleEntry = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteScheduleEntryUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteScheduleEntryMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteScheduleEntry>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteScheduleEntry>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteScheduleEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteScheduleEntry>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteScheduleEntry(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteScheduleEntryMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteScheduleEntry>>
>;

export type DeleteScheduleEntryMutationError = ErrorType<void>;

/**
 * @summary Delete a schedule entry
 */
export const useDeleteScheduleEntry = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteScheduleEntry>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteScheduleEntry>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteScheduleEntryMutationOptions(options));
};

/**
 * @summary Get overall dashboard stats (squads, total check-ins, top streaks, activity feed)
 */
export const getGetDashboardUrl = () => {
  return `/api/dashboard`;
};

export const getDashboard = async (
  options?: RequestInit,
): Promise<Dashboard> => {
  return customFetch<Dashboard>(getGetDashboardUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetDashboardQueryKey = () => {
  return [`/api/dashboard`] as const;
};

export const getGetDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboard>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getDashboard>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboard>>> = ({
    signal,
  }) => getDashboard({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDashboard>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetDashboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDashboard>>
>;
export type GetDashboardQueryError = ErrorType<unknown>;

/**
 * @summary Get overall dashboard stats (squads, total check-ins, top streaks, activity feed)
 */

export function useGetDashboard<
  TData = Awaited<ReturnType<typeof getDashboard>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getDashboard>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetDashboardQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * WI#30 — the in-app Billing entry. Same checks as GET /billing/portal, but answers with JSON so the app can show the server's message when billing can't be managed here, instead of navigating the customer onto a raw error.
 * @summary Get a single-use link into the PeakCommerce customer portal (change plan, cancel)
 */
export const getGetBillingPortalSessionUrl = () => {
  return `/api/billing/portal-session`;
};

export const getBillingPortalSession = async (
  options?: RequestInit,
): Promise<BillingPortalSession> => {
  return customFetch<BillingPortalSession>(getGetBillingPortalSessionUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetBillingPortalSessionQueryKey = () => {
  return [`/api/billing/portal-session`] as const;
};

export const getGetBillingPortalSessionQueryOptions = <
  TData = Awaited<ReturnType<typeof getBillingPortalSession>>,
  TError = ErrorType<BillingPortalUnavailable>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBillingPortalSession>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBillingPortalSessionQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBillingPortalSession>>
  > = ({ signal }) => getBillingPortalSession({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBillingPortalSession>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetBillingPortalSessionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBillingPortalSession>>
>;
export type GetBillingPortalSessionQueryError =
  ErrorType<BillingPortalUnavailable>;

/**
 * @summary Get a single-use link into the PeakCommerce customer portal (change plan, cancel)
 */

export function useGetBillingPortalSession<
  TData = Awaited<ReturnType<typeof getBillingPortalSession>>,
  TError = ErrorType<BillingPortalUnavailable>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBillingPortalSession>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetBillingPortalSessionQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
